import { Button } from '@mui/material'
import { appStore } from '@tom-ui/utils'
import { EnforcementMode, OccupancyType, StackInIssueCheckResponse } from '@yard/app/api'
import { occupancyService } from '@yard/services/occupancy.service'
import { yardIssueService } from '@yard/services/yard-issue.service'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { createElement } from 'react'
import { JourneyPosition } from '../models/journey-position'
import {
  getImdgStackInWarningHandoffPresentation,
  hasImdgStackInWarning,
} from '../utils/imdg-stack-in-warning-presentation.util'

export class YardPositionSelectorStore {
  journeyPositions: Record<number, JourneyPosition> = {}
  selectedBlockId?: string
  selectedBay?: number
  selectedRow?: number
  selectedTier?: number

  positionWarnings: StackInIssueCheckResponse[] = []

  constructor(private readonly yardBlockStore: YardBlockStore) {
    makeObservable(this, {
      journeyPositions: observable,
      selectedBlockId: observable,
      selectedBay: observable,
      selectedRow: observable,
      selectedTier: observable,
      positionWarnings: observable,

      setJourneyPosition: action,
      setSelectedBlockId: action,
      setSelectedBay: action,
      setSelectedRow: action,
      setSelectedTier: action,
      setPositionWarnings: action,

      yardBlocks: computed,
      isSelectedBlockStandard: computed,
      bays: computed,
      rows: computed,
      tiers: computed,
      hasBlockingPositionIssue: computed,
    })
  }

  setJourneyPosition(journeyId: number, position: JourneyPosition) {
    this.journeyPositions[journeyId] = position
  }

  async initFromJourney(journeyId: number, prefillPosition: OccupancyType) {
    try {
      const openJourneyPositions = await occupancyService.openJourneyPositions(journeyId)
      const currentPositionData = openJourneyPositions.find(j => j.journeyId === journeyId)

      if (!currentPositionData) return

      const positionsFromType =
        prefillPosition === OccupancyType.Occupied
          ? currentPositionData.occupancies
          : currentPositionData.reservations

      if (positionsFromType.length > 0) {
        const blockId = positionsFromType[0].blockId
        const positionToPrefill = positionsFromType[0]
        const isStandardBlock = this.yardBlockStore.yardBlocks[blockId]?.type === 'Standard'

        if (
          isStandardBlock &&
          (!positionToPrefill.bay || !positionToPrefill.row || !positionToPrefill.tier)
        )
          return

        this.setSelectedBlockId(blockId)

        if (isStandardBlock) {
          this.setSelectedBay(positionToPrefill.bay ?? undefined)
          this.setSelectedRow(positionToPrefill.row ?? undefined)
          this.setSelectedTier(positionToPrefill.tier ?? undefined)
        }

        this.setJourneyPosition(journeyId, {
          blockId,
          bay: positionToPrefill.bay ?? undefined,
          row: positionToPrefill.row ?? undefined,
          tier: positionToPrefill.tier ?? undefined,
        })
      }
    } catch (error) {
      console.error('Failed to initialize journey position', error)
    }
  }

  async checkPositionWarnings(journeyId: number, position: JourneyPosition) {
    const warnings = await yardIssueService.checkStackInIssues({
      forJourneyId: journeyId,
      blockId: position.blockId,
      position: {
        bay: position.bay!,
        row: position.row!,
        tier: position.tier!,
      },
    })

    this.setPositionWarnings(warnings)
  }

  async stackInJourney(journeyId: number, containerNumber?: string | null) {
    const yardBlockPosition = this.getJourneyPosition(journeyId)
    if (!yardBlockPosition) return

    const shouldShowDgWarningHandoff = hasImdgStackInWarning(this.positionWarnings)

    let position = undefined
    if (this.yardBlockStore.yardBlocks[yardBlockPosition.blockId]?.type === 'Standard') {
      position = {
        bay: yardBlockPosition.bay!,
        row: yardBlockPosition.row!,
        tier: yardBlockPosition.tier!,
      }
    }

    await appStore.triggerRequestWithoutLoader(() =>
      occupancyService.stackIn({
        blockId: yardBlockPosition.blockId,
        journeyId: journeyId,
        position: position,
      }),
    )

    if (shouldShowDgWarningHandoff) {
      const handoff = getImdgStackInWarningHandoffPresentation(containerNumber)
      const action = handoff.issuesUrl
        ? createElement(
            Button,
            { onClick: () => window.location.assign(handoff.issuesUrl!) },
            'View issues',
          )
        : undefined

      appStore.setShowAlert('warning', handoff.messageFallback, action)
    }
  }

  setSelectedBlockId(id?: string) {
    if (this.selectedBlockId !== id) {
      this.selectedBlockId = id
      this.setSelectedBay()
      this.setSelectedRow()
      this.setSelectedTier()
    }
  }

  setSelectedBay(bay?: number) {
    if (this.selectedBay !== bay) {
      this.selectedBay = bay
    }
  }

  setSelectedRow(row?: number) {
    if (this.selectedRow !== row) this.selectedRow = row
  }

  setSelectedTier(tier?: number) {
    if (this.selectedTier !== tier) this.selectedTier = tier
  }

  setPositionWarnings(positionWarnings?: StackInIssueCheckResponse[]) {
    this.positionWarnings = positionWarnings ?? []
  }

  getJourneyPosition(journeyId: number): JourneyPosition {
    return this.journeyPositions[journeyId]
  }

  reset() {
    this.setSelectedBlockId()
    this.setSelectedBay()
    this.setSelectedRow()
    this.setSelectedTier()
    this.setPositionWarnings()
  }

  get yardBlocks() {
    return this.yardBlockStore.items.slice().sort((a, b) => a.name.localeCompare(b.name))
  }

  get isSelectedBlockStandard() {
    if (!this.selectedBlockId) return false

    return this.yardBlockStore.yardBlocks[this.selectedBlockId]?.type === 'Standard'
  }

  get bays() {
    if (!this.selectedBlockId) return []

    const block = this.yardBlockStore.yardBlocks[this.selectedBlockId]
    if (!block) return []

    return block.bayNames.map((x, index) => ({
      label: x,
      value: index + 1,
    }))
  }

  get rows() {
    if (!this.selectedBlockId) return []

    const block = this.yardBlockStore.yardBlocks[this.selectedBlockId]
    if (!block) return []

    return block.rowNames.map((x, index) => ({
      label: x,
      value: index + 1,
    }))
  }

  get tiers() {
    if (!this.selectedBlockId) return []

    const block = this.yardBlockStore.yardBlocks[this.selectedBlockId]
    if (!block) return []

    return Array.from({ length: block.maxTiers }, (_, i) => i).map(x => ({
      label: (x + 1).toString(),
      value: x + 1,
    }))
  }

  get hasBlockingPositionIssue() {
    return this.positionWarnings.some(warning => warning.enforcementMode === EnforcementMode.Block)
  }

  get isJourneyPositionCompleted() {
    if (!this.isSelectedBlockStandard) {
      return !!this.selectedBlockId
    }

    return !!(this.selectedBlockId && this.selectedBay && this.selectedRow && this.selectedTier)
  }
}
